import React, { useState, useEffect, useRef } from "react";
import "./Fitness.css";
import {
  FaCheckCircle,
  FaDumbbell,
  FaHeartbeat,
  FaAppleAlt,
  FaBrain,
  FaUsers,
  FaFlask,
  FaBone,
  FaHandHoldingMedical,
  FaRunning,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import fitimg from "../assets/fit.png";
import premium1 from "../assets/premiumfit1.jpg";
import premium2 from "../assets/premiumfit2.jpg";
import premium3 from "../assets/premiumfit3.jpg";
import premium4 from "../assets/premiumfit4.jpg";
import premium5 from "../assets/premiumfit5.jpg";
import { Helmet } from "react-helmet-async";

const services = [
  {
    id: "strength",
    icon: <FaDumbbell />,
    tag: "Performance",
    title: "Strength & Conditioning",
    short: "Unlock peak athletic performance with science-backed training.",
    desc: "Designed for athletes of all levels, our programs focus on enhancing strength, speed, endurance, and injury resilience. Our qualified coaches deliver personalized plans tailored to your goals.",
    highlight: "Train smarter. Perform better.",
    link: "/services/strength",
  },
  {
    id: "physio",
    icon: <FaHandHoldingMedical />,
    tag: "Physiotherapy",
    title: "Physiotherapy & Pain Relief",
    short: "Expert physiotherapy for pain management and faster recovery.",
    desc: "Our certified physiotherapists treat acute and chronic pain conditions using manual therapy, dry needling, electrotherapy, and evidence-based exercise rehabilitation. From neck and back pain to sports injuries, we help you move pain-free.",
    highlight: "Move without pain. Live fully.",
    link: "/services/physiotherapy",
  },
  {
    id: "joint",
    icon: <FaBone />,
    tag: "Joint & Muscle",
    title: "Joint, Muscle & Ankle Rehab",
    short: "Targeted rehabilitation for joints, muscles, and ankle injuries.",
    desc: "Comprehensive rehab programs for knee, shoulder, hip, ankle, and wrist conditions. We address sprains, strains, ligament tears, and post-surgical recovery using progressive loading techniques and biomechanical correction.",
    highlight: "Restore mobility. Reclaim strength.",
    link: "/services/joint-rehab",
  },
  {
    id: "rehab",
    icon: <FaHeartbeat />,
    tag: "Recovery",
    title: "Injury Rehabilitation",
    short: "Expert care and personalized recovery plans.",
    desc: "Combining advanced physiotherapy, sports medicine, and performance tracking, we help you recover safely and return stronger from any injury — whether sports-related or post-operative.",
    highlight: "Recover safely. Return stronger.",
    link: "/services/rehab",
  },
  {
    id: "sports-rehab",
    icon: <FaRunning />,
    tag: "Sports Rehab",
    title: "Sports Injury Management",
    short: "Specialized care for ACL, rotator cuff, hamstring, and more.",
    desc: "Full-spectrum sports injury management from acute care through return-to-sport. We use functional movement screening, sport-specific rehab protocols, and graded return-to-play progressions for safe, fast recovery.",
    highlight: "Get back to the game. Stronger.",
    link: "/services/sports-injury",
  },
  {
    id: "nutrition",
    icon: <FaAppleAlt />,
    tag: "Nutrition",
    title: "Nutrition Services",
    short: "Clinical & sports nutrition for health and performance.",
    desc: "Personalized plans based on scientific evidence, bridging the gap between health and performance through comprehensive assessments and dietary strategies tailored to your body and goals.",
    highlight: "Fuel right. Live well.",
    link: "/services/nutrition",
  },
  {
    id: "psychology",
    icon: <FaBrain />,
    tag: "Mental Edge",
    title: "Sports Psychology",
    short: "Unlock peak performance through the power of the mind.",
    desc: "Enhance focus, build mental resilience, manage stress, and maintain motivation through personalized mental training and performance coaching for athletes at all levels.",
    highlight: "Mind sharp. Game sharper.",
    link: "/services/psychology",
  },
  {
    id: "group",
    icon: <FaUsers />,
    tag: "Group Training",
    title: "Group Fitness Classes",
    short: "Expert-led classes for every fitness level.",
    desc: "From HIIT to yoga, strength training and functional mobility — science-backed classes that combine motivation, community, and performance tracking to keep you progressing every session.",
    highlight: "Move together. Grow stronger.",
    link: "/services/group",
  },
  {
    id: "assessment",
    icon: <FaFlask />,
    tag: "Science",
    title: "Sports Science Assessment",
    short: "Comprehensive evaluations using advanced tools.",
    desc: "Measure athletic ability, biomechanics, and injury risks with expert analysis. Personalized insights to optimize training and enhance recovery using cutting-edge testing protocols.",
    highlight: "Measure. Optimize. Excel.",
    link: "/services/assessment",
  },
];

const stats = [
  { value: 800, suffix: "+", label: "Patients Treated" },
  { value: 98,   suffix: "%", label: "Satisfaction Rate" },
  { value: 5,    suffix: "+",  label: "Years of Experience" },
  { value: 24,    suffix: "/7",  label: "Support Available" },
];

const testimonials = [
  {
    name: "Smit Javiya",
    initials: "SJ",
    service: "Sports Science",
    stars: 5,
    quote:
      "One of the best places for Sports Science, Physiotherapy and Nutrition. I am a national athlete and the team helped me so much in improving my performance in preparation to the nationals. Thank you team Aikyam!",
  },
  {
    name: "Sharanya Sumant",
    initials: "SS",
    service: "Physiotherapy",
    stars: 5,
    quote:
      "I had backache and knee pain. I could feel the difference in the first few days of training — getting better day by day. The centre is fit for all ages. Thank you Aikyam team, you were such a great support.",
  },
  {
    name: "Solomon Ananda",
    initials: "SA",
    service: "Rehab",
    stars: 5,
    quote:
      "When I came I was not able to stand for a few minutes. After the rehab program I can now walk and stand for long durations. Staff — especially Diksha — watched every exercise carefully. Truly life-changing.",
  },
  {
    name: "Sanaath Bhat",
    initials: "SB",
    service: "Shoulder Rehab",
    stars: 5,
    quote:
      "I came for rehab after a shoulder dislocation. In just 2 months I had a wonderful experience. I got educated about my body and so many things I never realized before — I now know myself better.",
  },
  {
    name: "Rayan Christ",
    initials: "RC",
    service: "Sports Rehab",
    stars: 5,
    quote:
      "Best centre for specialised sports and fitness recoveries. Solved my arm issue in 5–6 days before a tournament. Highly recommended for goal-oriented athletes.",
  },
  {
    name: "Siddwin",
    initials: "SI",
    service: "Strength & Nutrition",
    stars: 5,
    quote:
      "Outstanding faculty in Strengthening (Prajwal/Varun) and Nutrition (Yadhu) — too professional in their fields. After going around multiple places, I can finally say I found one stop for all fitness and nutrition needs. Thank you Aikyam!",
  },
  {
    name: "Sharath Acharya",
    initials: "SA",
    service: "Physiotherapy",
    stars: 5,
    quote:
      "After a shoulder injury playing cricket in Oman, my doctor recommended surgery due to a bone crack. After visiting Aikyam I avoided surgery entirely. The team's approach was truly transformative.",
  },
  {
    name: "MAMATHA KORDCAL",
    initials: "MK",
    service: "Ankle Rehab",
    stars: 5,
    quote:
      "I joined Aikyam after a distal fibula fracture of the ankle. It was one of the best decisions for ankle rehab. I am grateful to everyone for their patience, concern and honest effort to get me back on my feet.",
  },
  {
    name: "Laisha Nagma Tonse",
    initials: "LT",
    service: "Sciatic Nerve Rehab",
    stars: 5,
    quote:
      "My daughter Raniya came here for sciatic nerve pain rehab. She had a really great recovery experience. The staff is friendly and the centre has various facilities. Overall it was absolutely worth it.",
  },
  {
    name: "Reyansh",
    initials: "RE",
    service: "Knee Rehab",
    stars: 5,
    quote:
      "Aikyam in Udupi is a blessing for people suffering from sports or accident injuries. I personally suffered a severe knee injury. The faculty is extremely welcoming and my recovery has been remarkable.",
  },
  {
    name: "Sannivas Shanbhag",
    initials: "SS",
    service: "Sports Injury",
    stars: 5,
    quote:
      "I had an incredible experience at Aikyam for my recovery. The team consists of helpful, well-trained professionals who truly understand the mechanics of sports injuries.",
  },
  {
    name: "Roydon Pinto",
    initials: "RP",
    service: "Badminton Performance",
    stars: 5,
    quote:
      "Training at Aikyam has really helped me both on the court and off it. They focus on fitness, stamina, speed, and sport-specific skills. Best part — they have all the equipment under one roof.",
  },
];

/* ── Scroll reveal helper ── */
function initScrollReveal() {
  const elements = document.querySelectorAll(".fit-reveal");
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add("fit-visible");
          observer.unobserve(e.target);
        }
      });
    },
    { threshold: 0.12 }
  );
  elements.forEach((el) => observer.observe(el));
}

/* ── Counter animation ── */
function initCounters() {
  document.querySelectorAll(".fit-stat__value[data-target]").forEach((el) => {
    const target = parseInt(el.dataset.target, 10);
    const suffix = el.dataset.suffix || "";
    const duration = 1600;
    const step = Math.ceil(target / (duration / 16));
    let current = 0;
    const timer = setInterval(() => {
      current = Math.min(current + step, target);
      el.textContent = current + suffix;
      if (current >= target) clearInterval(timer);
    }, 16);
  });
}

/* ── Single service card ── */
const FitCard = ({ svc, index }) => {
  const [expanded, setExpanded] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.style.transitionDelay = `${index * 0.07}s`;
  }, [index]);

  return (
    <div
      ref={ref}
      className="fit-card"
    >
      <div className="fit-card__header">
        <span className="fit-card__tag">{svc.tag}</span>
        <div className="fit-card__icon" aria-hidden="true">
          {svc.icon}
        </div>
      </div>

      <h3 className="fit-card__title">{svc.title}</h3>
      <p className="fit-card__short">{svc.short}</p>

      <div
        className={`fit-card__body${expanded ? " fit-card__body--open" : ""}`}
      >
        <p className="fit-card__desc">{svc.desc}</p>
        <div className="fit-card__highlight">
          <FaCheckCircle
            className="fit-card__highlight-icon"
            aria-hidden="true"
          />
          <span>{svc.highlight}</span>
        </div>
      </div>

      <div className="fit-card__footer">
        <button
          className="fit-btn-toggle"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
        >
          {expanded ? "Show Less ↑" : "Learn More →"}
        </button>
         <Link to="/booking" className="fit-link">
          <button className="fit-btn-book">
            <img
              src="/SVG/Consultation.svg"
              alt=""
              className="fit-button-icon"
            />
            Book Now
          </button>
        </Link>
      </div>
    </div>
  );
};

/* ── Testimonial card ── */
const TestiCard = ({ t }) => (
  <div className="fit-testi-card">
    <div className="fit-testi-stars">{"★".repeat(t.stars)}</div>
    <p className="fit-testi-quote">"{t.quote}"</p>
    <div className="fit-testi-author">
      <div className="fit-testi-avatar">{t.initials}</div>
      <div>
        <p className="fit-testi-name">{t.name}</p>
        <p className="fit-testi-location" style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <svg width="12" height="12" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Verified Google Review
        </p>
      </div>
      <span className="fit-testi-service">{t.service}</span>
    </div>
  </div>
);

/* ── Image Slideshow ── */
const slideshowImages = [
  {
    src: premium1,
    caption: "Strength & Conditioning",
    sub: "Science-backed training for every athlete",
  },
  {
    src: premium2,
    caption: "Physiotherapy & Rehab",
    sub: "Expert care — from pain relief to full recovery",
  },
  {
    src: premium3,
    caption: "Sports Science Assessment",
    sub: "Measure, optimise and exceed your potential",
  },
  {
    src: premium5,
    caption: "Nutrition & Wellness",
    sub: "Fuel your body with clinical precision",
  },
  {
    src: premium4,
    caption: "Group Fitness Classes",
    sub: "Train together — grow stronger",
  },
];

const ImageSlideshow = () => {
  const [current, setCurrent] = useState(0);
  const [prev, setPrev] = useState(null);
  const [transitioning, setTransitioning] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setPrev(current);
      setTransitioning(true);
      setCurrent((c) => (c + 1) % slideshowImages.length);
      setTimeout(() => {
        setPrev(null);
        setTransitioning(false);
      }, 900);
    }, 4500);
    return () => clearInterval(timer);
  }, [current]);

  const goTo = (i) => {
    if (i === current) return;
    setPrev(current);
    setTransitioning(true);
    setCurrent(i);
    setTimeout(() => { setPrev(null); setTransitioning(false); }, 900);
  };

  return (
    <div className="fit-slideshow fit-reveal">
      {/* Outgoing slide */}
      {prev !== null && (
        <div
          className="fit-slide fit-slide--out"
          style={{ backgroundImage: `url(${slideshowImages[prev].src})` }}
        />
      )}
      {/* Active slide */}
      <div
        key={current}
        className={`fit-slide fit-slide--in${transitioning ? " fit-slide--entering" : ""}`}
        style={{ backgroundImage: `url(${slideshowImages[current].src})` }}
      >
        <div className="fit-slide__overlay" />
        <div className="fit-slide__caption">
          <span className="fit-slide__eyebrow">Aikyam Sports Science · Udupi</span>
          <h3 className="fit-slide__title">{slideshowImages[current].caption}</h3>
          <p className="fit-slide__sub">{slideshowImages[current].sub}</p>
        </div>
      </div>
      {/* Dots */}
      <div className="fit-slide__dots">
        {slideshowImages.map((_, i) => (
          <button
            key={i}
            className={`fit-slide__dot${i === current ? " fit-slide__dot--active" : ""}`}
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};


const Fitness = () => {
  const statsRef = useRef(null);
  const [countersStarted, setCountersStarted] = useState(false);

  useEffect(() => {
    initScrollReveal();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !countersStarted) {
          setCountersStarted(true);
          initCounters();
        }
      },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, [countersStarted]);

  return (
    <>
<Helmet>
  <title>Best Physiotherapy in Udupi & Best Sports Rehab Center in Udupi | Aikyam</title>
  <meta
    name="description"
    content="Looking for the best physiotherapy center in Udupi? Aikyam Sports Science is the No. 1 clinic for sports injury rehab, best strength and conditioning (S&C), and sports science. Serving Udupi, Manipal, and Mangalore."
  />
  <meta
    name="keywords"
    content="best physiotherapy center in udupi, no 1 physiotherapy udupi, top sports rehab udupi, best strength and conditioning gym udupi, top rated physiotherapist manipal, best knee rehab mangalore, No 1 S&C center"
  />
  <link rel="canonical" href="https://aikyamsports.com/fitness" />

  {/* Open Graph */}
  <meta property="og:type" content="website" />
  <meta property="og:title" content="No. 1 Physiotherapy Clinic & Best Sports Rehab Center in Udupi | Aikyam" />
  <meta
    property="og:description"
    content="No. 1 destination for the best physiotherapy in Udupi , top-tier injury rehab, and elite strength training at Aikyam, Ajjarkad."
  />
  <meta property="og:image" content={fitimg} />
  <meta property="og:url" content="https://aikyamsports.com/fitness" />
  <meta property="og:locale" content="en_IN" />

  {/* Local business structured data */}
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "MedicalBusiness",
      "name": "Aikyam Sports Science",
      "image": "https://aikyamsports.com/fitimg.png",
      "description":
        "The No. 1 and best physiotherapy, injury rehabilitation, strength & conditioning, and sports science center serving Udupi, Manipal, and Mangalore.",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Ajjarkad",
        "addressLocality": "Udupi",
        "addressRegion": "Karnataka",
        "addressCountry": "IN"
      },
      "url": "https://aikyamsports.com/fitness",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "1300"
      }
    })}
  </script>
</Helmet>

    <section className="fit-section" aria-label="Aikyam Services">

      {/* ─── Header ─── */}
{/* ─── Header Update ─── */}
<div className="fit-header fit-reveal">
  <span className="fit-eyebrow">Udupi's No. 1 Clinic</span>
  <h2 className="fit-heading">
    Best Physiotherapy &amp; <em>Sports Science Services</em>
  </h2>
  <p className="fit-subtext">
    Ranked as the Best Physiotherapy &  No.1 Sports Rehab Center in Udupi. From immediate pain relief to elite strength and conditioning, we deliver top-rated care to athletes and patients across Udupi, Manipal, Mangalore, Karkala, and Kundapura.
  </p>
</div>

      {/* ─── Image Slideshow ─── */}
      <ImageSlideshow />

      {/* ─── Stats ─── */}
      <div className="fit-stats fit-reveal" ref={statsRef}>
        {stats.map((s) => (
          <div className="fit-stat" key={s.label}>
            <span
              className="fit-stat__value"
              data-target={s.value}
              data-suffix={s.suffix}
            >
              0{s.suffix}
            </span>
            <span className="fit-stat__label">{s.label}</span>
          </div>
        ))}
      </div>

      {/* ─── Service Cards ─── */}
      <div className="fit-grid fit-reveal">
        {services.map((svc, i) => (
          <FitCard key={svc.id} svc={svc} index={i} />
        ))}
      </div>

      {/* ─── Testimonials ─── */}
      <div className="fit-testimonials fit-reveal">
        <h3 className="fit-testimonials__heading">
          Trusted by People Across Udupi &amp; Beyond
        </h3>
        <p className="fit-testimonials__sub">
          Real results from our patients and athletes across Udupi, Mangalore,
          Karkala, Kundapura, and nearby regions.
        </p>

        {/* Google Reviews badge — replace YOUR_PLACE_ID with your actual Google Place ID */}
        <a
          className="fit-google-badge"
          href="https://www.google.com/maps/search/?api=1&query=Aikyam+Sports+Science+Ajjarkad+Udupi"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="View our Google Reviews"
        >
          {/* Google G logo */}
          <svg className="fit-google-badge__logo" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          <span className="fit-google-badge__stars">★★★★★</span>
          <span style={{ fontWeight: 600, fontSize: "13px" }}>4.9</span>
          <span className="fit-google-badge__label">on Google Reviews</span>
          <span className="fit-google-badge__count">· See all reviews →</span>
        </a>

        <div className="fit-testi-grid">
          {testimonials.map((t) => (
            <TestiCard key={t.name} t={t} />
          ))}
        </div>
      </div>

      {/* ─── Why Aikyam strip ─── */}
      <div className="fit-why fit-reveal" style={{ marginTop: "72px" }}>
        <div className="fit-why__left">
          <img
            src={fitimg}
            alt="Athletes training at Aikyam"
            onError={(e) => {
              e.target.src =
                "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=600&q=80";
            }}
          />
        </div>

        <div className="fit-why__right">
          <h3 className="fit-why__heading">Why Train at Aikyam?</h3>
          <p className="fit-why__sub">
            As the No. 1 rated physiotherapy and sports performance center on the coast, discover the elite care and top-tier strength protocols that make us the best choice for your recovery.
          </p>

          <div className="fit-why-point">
            <FaCheckCircle className="fit-why-icon" aria-hidden="true" />
            <div>
              <h4>Expert Physios &amp; Sports Scientists</h4>
              <p>
                Qualified professionals delivering evidence-based physiotherapy,
                injury rehab, and personalized training plans.
              </p>
            </div>
          </div>

          <div className="fit-why-point">
            <FaCheckCircle className="fit-why-icon" aria-hidden="true" />
            <div>
              <h4>State-of-the-Art Equipment</h4>
              <p>
                Train and recover with advanced fitness, rehab, and assessment
                tools to maximize results and track progress precisely.
              </p>
            </div>
          </div>

          <div className="fit-why-point">
            <FaCheckCircle className="fit-why-icon" aria-hidden="true" />
            <div>
              <h4>All Levels Welcome — Everyone Gets Better</h4>
              <p>
                From chronic pain patients to elite athletes — we serve Udupi,
                Manipal, Mangalore, Karkala, Kundapura, and the entire coast.
              </p>
            </div>
          </div>

          <Link to="/booking" className="fit-link">
            <button className="fit-btn-book fit-btn-book--large">
              <img
                src="/SVG/Consultation.svg"
                alt=""
                className="fit-button-icon"
              />
              Book a Free Consultation
            </button>
          </Link>
        </div>
      </div>
    </section>
    </>
  );
};

export default Fitness;